import { Alert, Flex, Heading, View } from "@instructure/ui";
import c3 from "c3";
import * as d3 from "d3";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { DashboardService } from "../../services/DashboardService.js";
import { FeatureContext } from "../../utils/Context.js";
import { sort } from "../../utils/utils.js";
import Legend from "../Cards/Legend.js";

const BarChart = (props) => {
  const { bindto, size, padding, filters, trigger, barChartWidth } = props;
  const features = useContext(FeatureContext);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [jsonGraphData, setJsonGraphData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [title, setTitle] = useState("");
  const [prevSubject, setPrevSubject] = useState(filters.subject);
  const [prevLoType, setPrevLoType] = useState(filters.LOType);
  const [chart, setChart] = useState(null);

  const finalizeFlag =
    features.find((f) => f.name === "finalize_results_flag")?.value === "TRUE";

  function determineContext(fetchFinalized) {
    if (fetchFinalized) {
      return null;
    } else {
      return "canvas_course_embedded,external_assessment,file_upload";
    }
  }

  const barChartUpdate = () => {
    if (!dataLoaded) {
      setPrevSubject(filters.subject);
      setPrevLoType(filters.LOType);
      DashboardService.getDashboardResults(
        filters.LOType,
        filters.subject,
        "bar",
        filters.terms,
        filters.courseLevels,
        filters.masteryResults,
        filters.categories,
        determineContext(finalizeFlag),
        null,
        filters.variety,
        null,
        finalizeFlag
      ).then((res) => {
        let json_data = [];
        if (res["results"]["ac_subject_results"].length > 0) {
          const categories = res["results"]["ac_subject_results"][0];
          setCategories(categories);
          if (filters.LOType === "ISLO") {
            json_data = getIsloJsonData(res, categories);
          } else {
            json_data = getPsloJsonData(res, categories);
          }
        }
        setJsonGraphData(json_data);
        setTitle(
          filters.masteryResults === "true"
            ? t("dashboard.masteryResultsAvgFor") + " " + filters.subject
            : t("dashboard.assignmentResultsAvgFor") + " " + filters.subject
        );
        setDataLoaded(true);
      });
    }
  };

  const getIsloJsonData = (res, categories) => {
    const json_data = [];
    const all_sub_key_name = res["results"]["all_subjects_results"][1][0];
    const ac_sub_key_name = res["results"]["ac_subject_results"][1][0];
    categories.forEach((category) => {
      const cat_index_all =
        res["results"]["all_subjects_results"][0].indexOf(category) + 1;
      const cat_index_ac =
        res["results"]["ac_subject_results"][0].indexOf(category) + 1;
      const all_sub_result =
        res["results"]["all_subjects_results"][1][cat_index_all];
      const ac_sub_result =
        res["results"]["ac_subject_results"][1][cat_index_ac];
      const category_json_entry = {
        lo: category,
        [all_sub_key_name]:
          all_sub_result && cat_index_all
            ? Number(all_sub_result.toFixed(2))
            : all_sub_result,
        [ac_sub_key_name]:
          ac_sub_result && cat_index_ac
            ? Number(ac_sub_result.toFixed(2))
            : ac_sub_result,
      };
      json_data.push(category_json_entry);
    });
    return json_data;
  };

  const getPsloJsonData = (res, categories) => {
    const json_data = [];
    const ac_sub_key_name = res["results"]["ac_subject_results"][1][0];
    categories.forEach((category) => {
      const cat_index_ac =
        res["results"]["ac_subject_results"][0].indexOf(category) + 1;
      const ac_sub_result =
        res["results"]["ac_subject_results"][1][cat_index_ac];
      const all_sub_json_entry = {
        lo: category,
        [ac_sub_key_name]:
          ac_sub_result && cat_index_ac
            ? Number(ac_sub_result.toFixed(2))
            : ac_sub_result,
      };
      json_data.push(all_sub_json_entry);
    });
    return json_data;
  };

  const makeChart = () => {
    setChart(
      c3.generate({
        data: {
          json: sort(jsonGraphData, (item) => item.lo),
          keys: { value: Object.keys(jsonGraphData[0]).slice(1) },
          type: "bar",
        },
        axis: {
          x: {
            type: "category",
            categories: sort(categories),
            tick: { multiline: true, multilineMax: 3 },
          },
          y: {
            label: {
              text:
                filters.masteryResults === "false"
                  ? t("dashboard.avgAssignmentGrade")
                  : t("dashboard.percentAchievedMastery"),
              position: "outer-middle",
            },
            min: 5,
            max: 100,
            tick: {
              values: [0, 20, 40, 60, 80, 100],
              format: function (d) {
                return d + "%";
              },
            },
          },
        },
        legend: { show: false },
        padding: { bottom: padding },
        tooltip: {
          grouped: false, // Default true
          format: {
            title: function (d) {
              return categories[d];
            },
          },
        },
        color: { pattern: ["#8ab1b2", "#b4c6b0"] },
        size: size,
        bindto: document.getElementById(bindto),
        bar: { width: { ratio: 0.5 } },
      })
    );
  };

  useEffect(() => {
    if (filters.subject) {
      if (trigger) {
        setDataLoaded(false);
        setJsonGraphData(null);
        setCategories([]);
      }
      barChartUpdate();
      if (dataLoaded && jsonGraphData && jsonGraphData[0]) {
        makeChart();
      }
    }
  }, [jsonGraphData, filters.subject, dataLoaded, trigger]);

  // This useEffect does everything that we just need to happen
  // on the re-rendering of the chart. These 2 things are:
  useEffect(() => {
    // 1: make the legend, re-rendering on every re-render of the chart
    if (chart && jsonGraphData && d3.select("[id^='ILOChart']")._groups[0][0]) {
      const legendItems = Object.keys(jsonGraphData[0]).slice(1);
      Legend({
        chart: chart,
        chartElementId: "ILOChart",
        legendClassName: "av-chart-legend",
        legendItems: legendItems,
      });
      // 2: resize the chart on render to fit the full allowance given
      chart.resize();
    }
  }, [chart, barChartWidth]);

  return (
    <>
      <Flex.Item size={barChartWidth} padding="0 small">
        {filters.subject && categories && categories.length > 0 ? (
          <View>
            <Heading as="h4" level="h4">
              {title}
            </Heading>

            <div id={bindto} />
          </View>
        ) : dataLoaded && jsonGraphData && categories && !trigger ? (
          <Alert variant="info">
            {t("notifications.assessments.noDataWithSubjectBar") +
              prevSubject +
              " " +
              prevLoType.replace("S", "") +
              "s"}
          </Alert>
        ) : (
          <Skeleton style={{ height: 64, borderRadius: 5, marginBottom: 10 }} />
        )}
      </Flex.Item>
    </>
  );
};

export default BarChart;
